import { useEffect, useRef } from 'react';
import { getActive, setActive, clearActive } from '@Utils/focusManager';

// usePostActionFocus
// - id: unique id string for this control
// - dependentKeys: array of dependencies which trigger focus attempts (e.g., selectedValue, resultsPerPage)
// - forwardedRef: optional ref to the underlying DOM element (preferred)
// - avoidIfActiveSelector: optional CSS selector; if document.activeElement is inside this selector, skip focusing
export function usePostActionFocus({ id, dependentKeys = [], forwardedRef = null, avoidIfActiveSelector = null }) {
  const ref = useRef(null);
  const timersRef = useRef([]);

  useEffect(() => {
    const active = getActive();
    if (active !== id) return;

    // If caller requested avoiding focus when a particular area is active, bail out
    if (avoidIfActiveSelector) {
      const activeEl = document.activeElement;
      if (activeEl && activeEl.closest && activeEl.closest(avoidIfActiveSelector)) {
        clearActive();
        return;
      }
    }

    const attemptFocus = (element) => {
      if (!element || typeof element.focus !== 'function') {
        return false;
      }

      try {
        element.focus();
      } catch (e) {
        return false;
      }
      return document.activeElement === element || document.activeElement === element.firstChild;
    };

    const tryFocusOnce = () => {
      // If this hook instance is no longer the active one, abort and clear its timers
      if (getActive() !== id) {
        if (timersRef.current && timersRef.current.length) {
          timersRef.current.forEach((t) => clearTimeout(t));
          timersRef.current = [];
        }
        return false;
      }

      // Try forwarded ref first
      const fwd = forwardedRef && forwardedRef.current;
      if (attemptFocus(fwd)) {
        clearActive();
        return true;
      }

      // Try hook ref directly
      const el = ref.current;
      if (attemptFocus(el)) {
        clearActive();
        return true;
      }

      // Fallback: if the ref is a wrapper, query for focusable elements inside it
      if (el && el.querySelector) {
        const focusable = el.querySelector('button, select, input, textarea, [tabindex]:not([tabindex="-1"])');
        if (attemptFocus(focusable)) {
          clearActive();
          return true;
        }
      }

      return false;
    };

    // Retry mechanism to handle race conditions with components that also focus themselves.
    // We'll attempt a few times with small backoff. If any attempt succeeds we stop and clear timers.
    const delays = [0, 50, 100, 200, 500];
    const timers = [];
    delays.forEach((d) => {
      const t = setTimeout(() => {
        const success = tryFocusOnce();
        if (success) {
          // clear pending timers
          timers.forEach((tt) => clearTimeout(tt));
        }
      }, d);
      timers.push(t);
    });

    // store timers so we can clear them on cleanup
    timersRef.current = timers;

    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current = [];
    };
    // dependentKeys intentionally omitted from exhaustive-deps lint rule to allow array control
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependentKeys);

  const markActive = () => setActive(id);

  return { ref, markActive };
}

export default usePostActionFocus;
